'use client';

import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';

/**
 * Parses a display target string like "$2.4M+" into its constituent parts.
 * Returns `{ prefix, numeric, suffix }` so the animator only touches the
 * number while prefix and suffix remain stable throughout the animation.
 *
 * Examples:
 *   "$2.4M+"  →  { prefix: "$", numeric: 2.4, suffix: "M+" }
 *   "40+"     →  { prefix: "",  numeric: 40,  suffix: "+"  }
 *   "95%"     →  { prefix: "",  numeric: 95,  suffix: "%"  }
 *   "10×"     →  { prefix: "",  numeric: 10,  suffix: "×"  }
 */
function parseTarget(target: string): {
  prefix: string;
  numeric: number;
  suffix: string;
  hasDecimal: boolean;
} {
  // Match optional leading non-numeric prefix, then a number (int or decimal),
  // then everything that follows as the suffix.
  const match = /^([^0-9]*)([0-9]+(?:\.[0-9]+)?)(.*)$/.exec(target);
  if (!match) {
    return { prefix: '', numeric: 0, suffix: target, hasDecimal: false };
  }
  const prefix = match[1] ?? '';
  const numericStr = match[2] ?? '0';
  const suffix = match[3] ?? '';
  const numeric = parseFloat(numericStr);
  const hasDecimal = numericStr.includes('.');
  return { prefix, numeric, suffix, hasDecimal };
}

/** cubic-bezier(0.16, 1, 0.3, 1) evaluated at t ∈ [0,1] via numerical approximation. */
function easeOut(t: number): number {
  // Approximate via a polynomial that matches the cubic bezier (0.16, 1, 0.3, 1).
  // This curve has a fast start and a long settling tail — identical to Linear's
  // signature easing. We use the JS equivalent so counter feels cohesive with CSS.
  return 1 - Math.pow(1 - t, 3) * (1 + 3 * t * 0.2);
}

const DURATION_MS = 1200;

/**
 * Animates a numeric display string from 0 toward its target when the
 * referenced element first enters the viewport.
 *
 * @param target  Human-readable stat string, e.g. "40+", "$2.4M+", "95%", "10×".
 * @returns  `[ref, displayValue]` — attach `ref` to the element to observe;
 *           render `displayValue` as the current animated text.
 *
 * Reduced-motion: returns the target immediately, no animation.
 */
export function useCounter(
  target: string,
): [React.RefObject<HTMLSpanElement | null>, string] {
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLSpanElement | null>(null);
  const [animated, setAnimated] = useState('');

  useEffect(() => {
    if (reduced) return;

    const el = ref.current;
    if (!el) return;

    const { prefix, numeric, suffix, hasDecimal } = parseTarget(target);
    let startTime: number | null = null;
    let rafId: number;
    let started = false;

    const tick = (now: number) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / DURATION_MS, 1);
      const eased = easeOut(progress);
      const current = numeric * eased;
      const formatted = hasDecimal ? current.toFixed(1) : Math.floor(current).toString();
      setAnimated(`${prefix}${formatted}${suffix}`);
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Guard: if already started (e.g. React Strict Mode double-invoke),
        // do not re-fire the animation.
        if (entry?.isIntersecting && !started) {
          started = true;
          observer.disconnect();
          rafId = requestAnimationFrame(tick);
        }
      },
      {
        // Lower threshold so tall stat elements still trigger even when they
        // don't fill 50% of the viewport. rootMargin fires slightly before the
        // bottom edge enters so the count starts as the number scrolls in.
        threshold: 0.3,
        rootMargin: '0px 0px -10% 0px',
      },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [target, reduced]);

  // When reduced-motion is on, render the final target immediately. Otherwise
  // start blank and let the rAF loop fill it in. This shape avoids ever
  // calling setState synchronously inside an effect (React 19 lint rule).
  const display = reduced ? target : (animated || target);

  return [ref, display];
}
